import i18n from "@/i18n";
import BFValueDisplay from "@/modules/abstract-ui/data/value-display/BFValueDisplay";
import StringUtils from "@/utils/StringUtils";
import moment from "moment";
import useVacancyActivity from "../../../hooks/useVacancyActivity";
import {
  VacancyApplicant,
  VacancyApplicantBase,
  VacancyApplicantEmploymentStatusLevel,
  VacancyApplication,
} from "../../../model/VacancyApplicant.interface";
import FilePreviewComponent from "@/components/FilePreviewComponent/FilePreviewComponent";
import { AssetTypes } from "@/model/AssetTypes";
import { APActivity } from "@/apps/tatar/activityApp/ActivityInterfaces";
import { ActivityApplicationConstants } from "@/apps/tatar/activityApp/ActivityHooks";
import BFMessageBox from "@/modules/abstract-ui/general/Message/BFMessageBox";
import VacancyApplicantEmployerActions from "./VacancyApplicantEmployerActions";

type Props = {
  application: VacancyApplication;
  applicant: VacancyApplicant;
  activity: APActivity;
  constants: ActivityApplicationConstants;
  level?: VacancyApplicantEmploymentStatusLevel;
  renderGuarantor?: boolean;
  onOpenGuarantorModal?: () => void;
  closeModal?: () => void;
};

const VacancyApplicantEmployerPart = (props: Props) => {
  const {
    application,
    applicant,
    activity,
    constants,
    level,
    onOpenGuarantorModal,
    renderGuarantor = false,
    closeModal,
  } = props;
  const { vacancyApplicantEmploymentStatusOptions } = useVacancyActivity();

  const applicantData = renderGuarantor ? applicant.guarantor : applicant;

  const renderRentCoverateCertificate = () => {
    if (
      applicantData.rentCoverageCertificate === undefined ||
      applicantData.rentCoverageCertificate === null
    ) {
      return null;
    }
    const rentCoverageCertificateAttachments = Array.isArray(
      applicantData.rentCoverageCertificate
    )
      ? applicantData.rentCoverageCertificate?.filter((p) => p !== null)
      : applicantData.rentCoverageCertificate
      ? [applicantData.rentCoverageCertificate]
      : [];

    if (rentCoverageCertificateAttachments.length === 0) {
      return null;
    }
    return (
      <div>
        <div className="__h3">
          {i18n.t(
            "apTemplate:Vacancy.Applicant.Attachments.RentCoverageCertificate",
            "Bescheinigung Jobcenter"
          )}
        </div>
        {rentCoverageCertificateAttachments?.map((p) => {
          if (!p) {
            return null;
          }
          return (
            <FilePreviewComponent
              type="cdn"
              asset={application}
              assetField={`${
                p.migrationFieldName
                  ? p.migrationFieldName
                  : "data.rentCoverageCertificate"
              }`}
              assetType={`${
                p.migrationAssetName
                  ? p.migrationAssetName
                  : AssetTypes.ActivityRelated.VacancyApplication
              }`}
              cdnID={p.linkToCdn}
              height={60}
              width={60}
              hasFolderPermissions
              dimension="thumb"
            />
          );
        })}
      </div>
    );
  };

  const renderJobDetails = (applicantData: VacancyApplicantBase) => {
    const employmentStatus = applicantData?.employmentInfo?.status || "";

    const label =
      vacancyApplicantEmploymentStatusOptions.find(
        (option) => option.key === employmentStatus
      )?.label || employmentStatus;

    if (
      employmentStatus === "employed" ||
      employmentStatus === "civilServant" ||
      employmentStatus === "self-employed"
    ) {
      return (
        <div>
          <div className="__grid __grid-cols-3 __grid-gap-10 __justify-items-start padding-top-10 __align-items-center">
            <BFValueDisplay
              label={i18n.t(
                "apTemplate:Vacancy.Applicant.Label.Job",
                "Berufl. Tätigkeit"
              )}
              value={label}
            />
            <BFValueDisplay
              label={i18n.t(
                "apTemplate:Vacancy.Applicant.Label.IncomeNet",
                "Nettoeinnkommen"
              )}
              value={StringUtils.formatCurrency(
                applicantData.employmentInfo?.netIncome || 0
              )}
            />
            <BFValueDisplay
              label={i18n.t(
                "apTemplate:Vacancy.Applicant.Label.JobTitle",
                "Berufsbezeichnung"
              )}
              value={applicantData.employmentInfo?.jobName || "-"}
            />
          </div>
          {employmentStatus === "employed" && (
            <>
              <div className="__grid __grid-cols-3 __grid-gap-10 __justify-items-start padding-top-10 __align-items-center">
                <BFValueDisplay
                  label={i18n.t(
                    "apTemplate:Vacancy.Applicant.Label.EmployedSince",
                    "Beschäftigt seit"
                  )}
                  value={
                    moment(applicantData.employmentInfo?.start).format(
                      "DD.MM.YYYY"
                    ) || "-"
                  }
                />
                <BFValueDisplay
                  label={i18n.t(
                    "apTemplate:Vacancy.Applicant.Label.EmploymentTemporary",
                    "Befristet"
                  )}
                  value={
                    applicantData.employmentInfo?.temporary
                      ? i18n.t("Global.Yes", "Ja")
                      : i18n.t("Global.No", "Nein")
                  }
                />
              </div>
              <div className="__h3 padding-top-10">
                {i18n.t(
                  "apTemplate:Vacancy.Applicant.Label.Employer",
                  "Arbeitgeber"
                )}
              </div>
              <div className="__grid __grid-cols-3 __grid-gap-10 __justify-items-start padding-top-10 __align-items-center">
                <BFValueDisplay
                  label={i18n.t(
                    "apTemplate:Vacancy.Applicant.Label.EmployerName",
                    "Arbeitgeber"
                  )}
                  value={applicantData.employmentInfo?.employer?.name || "-"}
                />
                <BFValueDisplay
                  label={i18n.t(
                    "apTemplate:Vacancy.Applicant.Label.EmployerPhone",
                    "Telefon"
                  )}
                  value={applicantData.employmentInfo?.employer?.phone || "-"}
                />
              </div>
              <div className="__grid __grid-cols-3 __grid-gap-10 __justify-items-start padding-top-10 __align-items-center">
                <BFValueDisplay
                  label={i18n.t(
                    "apTemplate:Vacancy.Applicant.Label.Street",
                    "Adresse"
                  )}
                  value={`${
                    applicantData.employmentInfo?.employer?.street || "-"
                  } ${
                    applicantData.employmentInfo?.employer?.streetNumber || ""
                  }`}
                />
                <BFValueDisplay
                  label={i18n.t(
                    "apTemplate:Vacancy.Applicant.Label.ZipCode",
                    "PLZ"
                  )}
                  value={applicantData.employmentInfo?.employer?.zipCode}
                />
                <BFValueDisplay
                  label={i18n.t(
                    "apTemplate:Vacancy.Applicant.Label.City",
                    "Ort"
                  )}
                  value={applicantData.employmentInfo?.employer?.city}
                />
              </div>
            </>
          )}
        </div>
      );
    }

    return (
      <>
        <BFValueDisplay
          label={i18n.t(
            "apTemplate:Vacancy.Applicant.Label.Job",
            "Berufl. Tätigkeit"
          )}
          value={label}
        />
        {employmentStatus === "lookingForWork" && (
          <>
            {renderGuarantor ? (
              <BFMessageBox type="warning">
                {i18n.t(
                  "apTemplate:Vacancy.Applicant.Label.GuarantorLookgingForWork",
                  "Der Bürge hat angegeben nicht beschäftigt zu sein. Bitte prüfen Sie die Angaben."
                )}
              </BFMessageBox>
            ) : (
              renderRentCoverateCertificate()
            )}
          </>
        )}
      </>
    );
  };

  const renderJobStatusLevel = () => {
    if (renderGuarantor) {
      return null;
    }

    const needsAdditionalData =
      level?.isGuarantorNeeded || level?.isRentCoverageNeeded;
    if (!level || (level.level === "full" && !needsAdditionalData)) {
      return null;
    }

    const hasRentCoverageCertificate =
      applicantData.rentCoverageCertificate !== undefined &&
      applicantData.rentCoverageCertificate !== null;

    if (level.isRentCoverageNeeded && hasRentCoverageCertificate) {
      return null;
    }

    return (
      <BFMessageBox
        className="margin-top-20"
        type={`${
          level.level === "partially"
            ? "warning"
            : level.level === "warn"
            ? "error"
            : "success"
        }`}
        bordered
      >
        <VacancyApplicantEmployerActions
          activity={activity}
          application={application}
          applicant={applicant}
          constants={constants}
          level={level}
          onOpenGuarantorModal={onOpenGuarantorModal}
          closeModal={closeModal}
        />
      </BFMessageBox>
    );
  };

  const proofOfIncomeAttachments = applicantData.proofOfIncome;

  const hasProofOfIncome = proofOfIncomeAttachments?.length > 0;

  return (
    <div>
      <div className="__h3">
        {i18n.t("apTemplate:Vacancy.Applicant.Label.Job", "Beruf")}
      </div>
      {renderJobDetails(applicantData)}
      {renderJobStatusLevel()}
      {hasProofOfIncome && (
        <>
          <BFValueDisplay
            className="padding-top-10"
            label={i18n.t(
              "apTemplate:Vacancy.Applicant.Label.ProofOfIncome",
              "Einkommensbescheinigungen"
            )}
            value={
              <div className="__flex">
                {proofOfIncomeAttachments.map((p) => {
                  if (!p) {
                    return null;
                  }
                  return (
                    <FilePreviewComponent
                      type="cdn"
                      asset={application}
                      assetField={`${
                        p.migrationFieldName
                          ? p.migrationFieldName
                          : "data.proofOfIncome"
                      }`}
                      assetType={`${
                        p.migrationAssetName
                          ? p.migrationAssetName
                          : AssetTypes.ActivityRelated.VacancyApplication
                      }`}
                      cdnID={p.linkToCdn}
                      height={60}
                      width={60}
                      hasFolderPermissions
                      dimension="thumb"
                    />
                  );
                })}
              </div>
            }
          />
        </>
      )}
      {applicantData?.employmentInfo?.note && (
        <BFValueDisplay
          className="padding-top-10"
          label={i18n.t("apTemplate:Vacancy.Applicant.Label.JobNote", "Notiz")}
          value={applicantData?.employmentInfo?.note}
        />
      )}
    </div>
  );
};

export default VacancyApplicantEmployerPart;
